import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ArrowRight,
  Calendar,
  Download,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Layers,
  Mail,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackFormSubmit, openWebCenterDecisionCall } from '@/lib/analytics';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const downloadFormSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  company: z.string().min(1, 'Company is required'),
  title: z.string().optional(),
  honeypot: z.string().optional(),
});

type DownloadFormData = z.infer<typeof downloadFormSchema>;

const DownloadForm = ({ source }: { source: string }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DownloadFormData>({
    resolver: zodResolver(downloadFormSchema),
  });

  const onSubmit = async (data: DownloadFormData) => {
    if (data.honeypot) return;
    setIsSubmitting(true);
    const message = `WebCenter Decision Guide download request | Title: ${data.title || 'not provided'} | Source: ${source}`;
    try {
      const { error } = await supabase.from('contact_submissions').insert([
        {
          name: data.name,
          email: data.email,
          company: data.company,
          message,
        },
      ]);

      if (error) throw error;

      // Trigger SendGrid notification so Andrew + customer get the email confirmation.
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: data.name,
          email: data.email,
          company: data.company,
          message,
          honeypot: data.honeypot,
        },
      });
      if (emailError) console.error('Email notification failed:', emailError);

      trackFormSubmit('webcenter_decision_guide_download', { source });
      setIsSubmitted(true);
      toast({
        title: 'Decision Guide on the way',
        description:
          "We'll email the printable Decision Guide within 24 hours. Check your inbox.",
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Something went wrong',
        description: 'Please try again or email hello@ezcloud.ai directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="p-7 bg-accent/10 border border-accent/30">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-1" aria-hidden />
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              The Decision Guide is on the way
            </h3>
            <p className="text-gray-700">
              We'll email the printable PDF within 24 hours. While you wait, you can also{' '}
              <button
                onClick={() => openWebCenterDecisionCall('decision-guide-success')}
                className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
              >
                book a 30-minute WebCenter Decision Call
              </button>{' '}
              with {FOUNDER.name} to talk through your specific situation.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-7 bg-white border border-gray-200 shadow-md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="dg-name-${source}" className="text-gray-900">
              Name *
            </Label>
            <Input
              id={`dg-name-${source}`}
              {...register('name')}
              className="mt-1"
              placeholder="Your name"
              autoComplete="name"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <Label htmlFor={`dg-email-${source}`} className="text-gray-900">
              Work email *
            </Label>
            <Input
              id={`dg-email-${source}`}
              type="email"
              {...register('email')}
              className="mt-1"
              placeholder="you@company.com"
              autoComplete="email"
            />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`dg-company-${source}`} className="text-gray-900">
              Company *
            </Label>
            <Input
              id={`dg-company-${source}`}
              {...register('company')}
              className="mt-1"
              placeholder="Your company"
              autoComplete="organization"
            />
            {errors.company && <p className="text-sm text-red-600 mt-1">{errors.company.message}</p>}
          </div>
          <div>
            <Label htmlFor={`dg-title-${source}`} className="text-gray-900">
              Job title
            </Label>
            <Input
              id={`dg-title-${source}`}
              {...register('title')}
              className="mt-1"
              placeholder="e.g. AP Manager, CIO"
              autoComplete="organization-title"
            />
          </div>
        </div>
        <input
          type="text"
          {...register('honeypot')}
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          aria-hidden
        />
        <Button
          type="submit"
          size="lg"
          variant="accent"
          className="font-semibold w-full sm:w-auto"
          disabled={isSubmitting}
        >
          <Download className="mr-2 w-5 h-5" aria-hidden />
          {isSubmitting ? 'Sending...' : 'Get the printable Decision Guide'}
        </Button>
        <p className="text-xs text-gray-500">
          We'll email the PDF within 24 hours. No marketing list signup — single email with the
          guide attached.
        </p>
      </form>
    </Card>
  );
};

const WebCenterDecisionGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'The Oracle WebCenter AP December 2026 Decision Guide',
    description:
      'A structured decision guide for Oracle WebCenter AP customers facing the Oracle Fusion Middleware 12c Premier Support end of December 2026. Four forward paths, stack-specific considerations, decision framework, and vendor evaluation checklist.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/resources/webcenter-decision-guide',
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="The Oracle WebCenter AP December 2026 Decision Guide | EZ Cloud"
        description="A structured decision guide for Oracle WebCenter AP customers facing the Oracle Fusion Middleware 12c Premier Support end of December 2026. Four forward paths, stack-specific considerations, vendor evaluation checklist."
        canonical="https://ezcloud.ai/resources/webcenter-decision-guide"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/webcenter-decision-guide' },
          {
            name: 'WebCenter Decision Guide',
            url: 'https://ezcloud.ai/resources/webcenter-decision-guide',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-4xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <FileText className="w-4 h-4" aria-hidden />
              Decision Guide · December 2026 Cliff
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              The Oracle WebCenter AP
              <span className="block text-accent mt-2">December 2026 Decision Guide</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              A structured guide for finance, IT, and operational leaders running Oracle WebCenter
              AP automation today. Authored by {FOUNDER.name}, a 25-year Oracle WebCenter AP
              specialist.
            </p>
          </div>
        </section>

        {/* Email capture — top */}
        <section className="py-10 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-3xl">
            <DownloadForm source="top" />
          </div>
        </section>

        {/* Executive Summary */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl prose-styled">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Executive Summary
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              What every Oracle WebCenter AP customer needs to decide before December 2026
            </h2>
            <div className="space-y-5 text-gray-700 leading-relaxed text-lg">
              <p>
                Oracle Fusion Middleware 12c — the platform underneath every Oracle WebCenter
                product, including all of WebCenter AP — exits Premier Support in December 2026.
                Extended Support runs through December 2027 (unusually short at 12 months), and
                after that, the only Oracle-provided option is Market Driven Support, characterized
                in industry coverage as "an extraordinarily limited product."
              </p>
              <p>
                Every WebCenter AP customer in the world has to respond. There are four legitimate
                forward paths: lift-and-shift the existing WebCenter implementation to Oracle Cloud
                Infrastructure (<strong>Path A</strong>); upgrade in place to WebCenter 14c
                (<strong>Path A+</strong>); modernize off WebCenter to a modern AP automation
                platform that deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP
                (<strong>Path B</strong>); or run a hybrid that modernizes the AP layer while
                preserving WebCenter for other content management purposes (<strong>Path B+</strong>).
              </p>
              <p>
                This guide is the framework for choosing well. It walks through the forcing
                function, the four paths, the stack-specific considerations (FIPSA, WFR, ADF, SOA,
                Imaging), and a vendor evaluation checklist. The author is {FOUNDER.name}, who has
                spent 25 years inside Oracle WebCenter AP — implementing, customizing, and
                eventually modernizing implementations across 11g, 12c, and 14c.
              </p>
            </div>
          </div>
        </section>

        {/* Section 1: The forcing function */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Section 1
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              The forcing function — Oracle Fusion Middleware 12c support timeline
            </h2>
            <div className="space-y-5 text-gray-700 leading-relaxed">
              <p>
                Oracle's Lifetime Support Policy governs the support timeline for every Oracle
                product. For Fusion Middleware 12c — which is the platform underneath WebCenter
                Content (WCC), WebCenter Imaging (WCI), WebCenter Forms Recognition (WFR), WebCenter
                Enterprise Capture (WEC), WebCenter Portal (WCP), WebCenter Sites (WCS), and the
                FIPSA accelerator — the timeline is:
              </p>
              <div className="space-y-4 my-6">
                <Card className="p-5 bg-white border border-amber-200">
                  <p className="font-bold text-gray-900 mb-1">December 2026 — Premier Support ends</p>
                  <p className="text-sm text-gray-700">
                    After this date, Oracle stops shipping new features, OS certifications, and
                    database certifications. Your WebCenter stack is frozen at the
                    12.2.1.4 / 12.2.1.19 baseline.
                  </p>
                </Card>
                <Card className="p-5 bg-white border border-red-200">
                  <p className="font-bold text-gray-900 mb-1">December 2027 — Extended Support ends</p>
                  <p className="text-sm text-gray-700">
                    Per Oracle's Lifetime Support Policy. Unusually short at 12 months (typical is
                    24-36). Extended Support provides security updates and critical patches but not
                    general bug fixes.
                  </p>
                </Card>
                <Card className="p-5 bg-white border border-red-200">
                  <p className="font-bold text-gray-900 mb-1">Beyond 2027 — Market Driven Support</p>
                  <p className="text-sm text-gray-700">
                    Yearly-renewable, costs more than Extended Support, provides limited patching.
                    Designed as transition bridge, not destination. Estimated viable through 2030.
                  </p>
                </Card>
              </div>
              <p>
                The audit dimension matters as much as the technical dimension. Some industries —
                financial services, healthcare, government — require vendor-supported software in
                audit scope. Customers in regulated verticals face procurement-driven pressure to
                transition before audit cycles flag the support gap.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Four paths */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Section 2
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              The four forward paths
            </h2>
            <div className="space-y-8 text-gray-700 leading-relaxed">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Path A — Lift-and-shift to OCI Marketplace
                </h3>
                <p className="mb-3">
                  Move your existing WebCenter implementation to Oracle Cloud Infrastructure using
                  the Oracle WebCenter for OCI Marketplace images (released 24.11.1, available
                  since November 2024). The architecture, customizations, Forms Recognition
                  templates, SOA composites, and ADF customizations are preserved. The
                  infrastructure becomes OCI.
                </p>
                <p>
                  <strong className="text-gray-900">Timeline:</strong> 3-6 months typical.{' '}
                  <strong className="text-gray-900">Best fit:</strong> customers deferring strategic
                  decision; limited modernization budget; AP requirements unchanged.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Path A+ — Upgrade in place to WebCenter 14c
                </h3>
                <p className="mb-3">
                  Upgrade your existing WebCenter 12c implementation to the 14c release. 14c brings
                  cloud-native support, AI tools, and REST APIs, with 8 years of Oracle support.
                  Architecture preserved; new capabilities layered on.
                </p>
                <p>
                  <strong className="text-gray-900">Timeline:</strong> 6-12 months typical.{' '}
                  <strong className="text-gray-900">Best fit:</strong> customers with significant
                  existing WebCenter investment who want the longest runway without strategic
                  platform change.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Path B — Modernize off WebCenter to a modern AP automation platform
                </h3>
                <p className="mb-3">
                  Replace the WebCenter AP stack with a modern AI-driven AP automation platform
                  that deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP using
                  Oracle-supported integration patterns (Payables Open Interface on EBS, OIC + FBDI
                  on Fusion). Oracle ERP remains system of record; the AP automation layer
                  transitions to a modern platform.
                </p>
                <p>
                  <strong className="text-gray-900">Timeline:</strong> 4-9 months typical.{' '}
                  <strong className="text-gray-900">Best fit:</strong> customers ready for the
                  strategic decision; customers using December 2026 as the forcing function to
                  upgrade the AP operation, not just the infrastructure.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Path B+ — Hybrid (modernize AP, preserve WebCenter for other purposes)
                </h3>
                <p className="mb-3">
                  Some customers use WebCenter for content management beyond AP — records
                  management, document management, etc. In a hybrid path, the AP layer is
                  modernized to a different platform while the rest of WebCenter is preserved (and
                  separately migrated to OCI or upgraded to 14c on its own timeline).
                </p>
                <p>
                  <strong className="text-gray-900">Timeline:</strong> 4-9 months for the AP layer;
                  WebCenter Content track separate.{' '}
                  <strong className="text-gray-900">Best fit:</strong> customers with diverse
                  WebCenter usage where AP is the most-strained component.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Stack-specific considerations */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Section 3
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              Stack-specific considerations
            </h2>
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  FIPSA — Financial Imaging Processing Solutions Accelerator
                </h3>
                <p>
                  If your implementation is FIPSA-based, the modernization decision is about the
                  entire FIPSA stack: WebCenter Imaging (image capture/storage), WebCenter Forms
                  Recognition (template-based extraction), Oracle SOA Suite (BPEL workflow), and
                  the EBS Payables Open Interface (system-of-record posting). Each layer has its
                  own customization patterns and lifecycle dependencies. See the{' '}
                  <Link
                    to="/solutions/modernize-webcenter-fipsa"
                    className="text-primary font-semibold underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
                  >
                    FIPSA modernization page
                  </Link>{' '}
                  for the detailed mapping.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  WFR — WebCenter Forms Recognition
                </h3>
                <p>
                  If your data extraction layer is WFR, the modernization question is whether to
                  preserve the per-supplier template model (continues with Path A / A+) or replace
                  it with AI extraction that reads unseen invoices and learns from corrections
                  (Path B). The WFR template library represents years of supplier-specific business
                  knowledge — that knowledge transfers forward via a WFR Rules Migration Audit. See
                  the{' '}
                  <Link
                    to="/solutions/modernize-webcenter-forms-recognition"
                    className="text-primary font-semibold underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
                  >
                    WFR modernization page
                  </Link>{' '}
                  for the comparison.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ADF non-PO coding form
                </h3>
                <p>
                  The Oracle ADF non-PO coding form is one of the WebCenter AP customizations we
                  hear about most. It's powerful but development-heavy — small changes tend to
                  become development projects. In a Path B modernization, the ADF form is replaced
                  by a configurable web UI that AP staff can adjust without ADF development
                  cycles. In Path A / A+, the ADF form moves with the rest of WebCenter.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Oracle SOA Suite composites
                </h3>
                <p>
                  WebCenter AP workflows are typically implemented as BPEL composites running on
                  SOA Suite + WebLogic Server. The composites encode years of approval routing,
                  hold-code logic, and exception handling. In Path A / A+, the composites move
                  forward with the WebCenter stack. In Path B, the composites are read directly to
                  extract the business logic, which is then translated into a modern
                  configuration-driven workflow.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  WebCenter Imaging and Managed Attachments
                </h3>
                <p>
                  Invoice image storage and retrieval — including the Managed Attachments link from
                  the EBS or Fusion transaction back to the image — is a load-bearing audit
                  capability that's easy to overlook in a modernization. Confirm the retrieval path
                  is tested before any cutover. In Path B, images move to OCI Object Storage with
                  signed-URL retrieval from inside the ERP.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Decision matrix */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Section 4
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              The decision matrix — which path fits your situation?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Ask these eight questions about your current state:</p>
              <ol className="space-y-3 list-decimal pl-6 marker:text-accent marker:font-bold">
                <li>
                  Is your AP team satisfied with current WebCenter functionality, or are there
                  capability gaps (AI extraction, supplier email automation, modern UX) that the
                  business is asking for?
                </li>
                <li>
                  Is your supplier mix stable, or do you regularly onboard new suppliers requiring
                  WFR template authoring?
                </li>
                <li>
                  Do you have WebCenter expertise in-house, or are you relying on a small number of
                  individuals with concentrated departure risk?
                </li>
                <li>
                  Is the supplier-email triage workload (status chasing, missing PO numbers,
                  duplicate submissions) consuming a meaningful fraction of your AP team's capacity?
                </li>
                <li>
                  Is your ADF non-PO coding form requiring development cycles for routine changes?
                </li>
                <li>
                  Do you have modernization budget allocated in the next 6-12 months, or do you
                  need to defer the strategic decision?
                </li>
                <li>
                  Are you in a regulated industry where unsupported software in audit scope creates
                  procurement-driven pressure to transition?
                </li>
                <li>
                  Is an Oracle EBS &rarr; Fusion migration on your broader roadmap, where AP
                  modernization could be the unlock that makes the broader migration tractable?
                </li>
              </ol>
              <p>
                A majority of "yes" answers to the operational questions (1, 4, 5) points toward
                Path B. A "yes" to question 8 makes Path B especially leveraged. A "no" to question
                6 (no budget) typically points toward Path A as a bridge. A high concern on
                question 7 (regulated industry) accelerates whatever path is chosen.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Vendor evaluation checklist */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Section 5
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              Vendor evaluation checklist
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Whichever path you choose, the vendor evaluation should test for:</p>
              <ul className="space-y-3 list-none">
                {[
                  'Oracle-supported integration patterns (Payables Open Interface on EBS, OIC + FBDI on Fusion). No base-table writes.',
                  'WebCenter expertise across the full stack — Forms Recognition, SOA, WebLogic, FIPSA, ADF. Not just AP automation expertise.',
                  'Founder or senior-level engagement on the discovery and design phases. Junior consultants reading a complex WebCenter implementation produces incomplete plans.',
                  'Oracle endorsement signals — Reference Architecture publication on docs.oracle.com, Oracle technical case studies, Oracle Partner status.',
                  'References specific to WebCenter AP modernization (not just WebCenter consulting). The work shapes are different.',
                  'A productized assessment with a fixed-fee, fixed-scope deliverable — not an open-ended discovery engagement that bills hourly.',
                  'For Path B specifically: a modern AP automation platform with a published reference architecture, not a generic OCR + workflow product.',
                  'Clear deliverables you keep regardless of whether you proceed to implementation with the vendor.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2
                      className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
                      aria-hidden
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section 6: Engagement tiers */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Section 6
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              How EZ Cloud engages with WebCenter customers
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              The same 25-year founder-level WebCenter expertise applies across every engagement
              tier — whether you ultimately choose Path A, A+, B, or B+.{' '}
              <em className="text-gray-600">
                Pricing is established per-engagement based on stack complexity. Available on a
                Decision Call.
              </em>
            </p>
            <div className="space-y-5 text-gray-700 leading-relaxed">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Tier 1 — Modernization Assessment
                </h3>
                <p>
                  A productized, fixed-scope, fixed-fee engagement. Andrew reads your existing
                  WebCenter implementation directly — Forms Recognition templates, SOA composites,
                  WebLogic configuration, FIPSA accelerators, ADF customizations, EBS or Fusion
                  integration code — and delivers a forward-modernization plan that's yours to
                  keep. Universal entry point for any WebCenter AP customer facing the December
                  2026 decision.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Tier 2 — Architecture Review
                </h3>
                <p>
                  A standalone fixed-fee read of your existing WebCenter AP — Forms Recognition
                  rules, SOA composites, WebLogic configuration, FIPSA accelerators, ADF
                  customizations. Output: structured inventory of what you have and where
                  complexity lives. Lower commitment than the full Assessment; opens the door.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Tier 3 — Strategic Advisory Retainer
                </h3>
                <p>
                  Monthly retainer with Andrew, sized for customers working through a multi-quarter
                  WebCenter decision with internal stakeholders, procurement, and Oracle.
                  Strategic guidance on architecture, upgrade vs. modernization trade-offs, Fusion
                  migration sequencing.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Tier 4 — Specific WebCenter consulting outcomes
                </h3>
                <p>
                  Forms Recognition rules migration audits, FIPSA-specific assessments, WebCenter
                  Imaging archive migrations, 12c &rarr; 14c upgrade planning, OCI architecture
                  and security setup, custom SOA and ADF integration work. Project-based,
                  founder-led, scoped to a defined outcome.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Authority box */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto px-4 max-w-3xl">
            <Card className="p-8 bg-white border border-gray-200 shadow-md">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                About the author
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {FOUNDER.name}, Founder, EZ Cloud
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Andrew has spent 25 years inside Oracle accounts payable, with roots going back to
                the Oracle WebCenter content/imaging era. EZ Cloud is published on the{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
                >
                  Oracle Reference Architecture for OCI
                </a>{' '}
                and featured in{' '}
                <a
                  href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
                >
                  Oracle's own technical case studies
                </a>
                . Andrew is Oracle-certified: Oracle Cloud Infrastructure 2024 Generative AI
                Certified Professional and Oracle Cloud Infrastructure 2025 AI Foundations
                Associate, both verifiable on Oracle CertView.
              </p>
            </Card>
          </div>
        </section>

        {/* Email capture — bottom */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Get the printable version
            </p>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 leading-tight">
              Get this Decision Guide as a printable PDF
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              We'll email you a printable version of this guide — useful for sharing with internal
              stakeholders, procurement, or your Oracle account team.
            </p>
            <DownloadForm source="bottom" />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to talk through your specific situation?
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}. We'll walk through your WebCenter
              footprint and the four paths in the context of your operation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('decision-guide')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterDecisionGuide;
